export { apiBlob, apiJson, requireSuccess } from "./apiClient";

export interface ApiResponse {
  success: true;
  message: string;
}

export interface GenericErrorResponse {
  success: false;
  error?: string;
  warning?: string;
}

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  can_invite: boolean;
  current_lab: string | null;
  is_organisation_admin: boolean;
  is_auditor: boolean;
  organisation_id: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  agreedToTos: boolean;
  token?: string;
}

export interface AuthResponse<T = unknown> extends ApiResponse {
  data?: T;
  [key: string]: unknown;
}

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

const handleApiError = async (
  response: Response
): Promise<GenericErrorResponse> => {
  const errorData = await response.json().catch(() => ({}));
  const errorMessage =
    errorData.error || `HTTP error! status: ${response.status}`;

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:logout"));
  }

  return { success: false, error: errorMessage, ...errorData };
};

export const authApi = async <T extends ApiResponse = AuthResponse>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T | GenericErrorResponse> => {
  const url = endpoint.startsWith("http")
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      return await handleApiError(response);
    }

    const data = await response.json();
    return { success: true, ...data } as T;
  } catch (error) {
    console.error(`API call failed: ${endpoint}`, error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Network error. Please check your connection.",
    };
  }
};

export class AuthService {
  static async login(
    email: string,
    password: string,
    remember: boolean = false
  ): Promise<AuthResponse<User>> {
    const response = (await authApi("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, remember }),
    })) as AuthResponse<User> | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async register(payload: RegisterPayload): Promise<AuthResponse> {
    const { email, password, firstName, lastName, agreedToTos, token } = payload;
    const response = (await authApi("/auth/register", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        agreed_to_tos: agreedToTos,
        ...(token && { token }),
      }),
    })) as AuthResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async logout(): Promise<ApiResponse> {
    const response = (await authApi("/auth/logout", {
      method: "POST",
    })) as ApiResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async updateProfileNames(
    firstName: string,
    lastName: string
  ): Promise<AuthResponse<User>> {
    const response = (await authApi("/auth/profile", {
      method: "PATCH",
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
      }),
    })) as AuthResponse<User> | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response as AuthResponse<User>;
  }

  static async getCurrentUser(): Promise<AuthResponse<User>> {
    const response = (await authApi("/auth/user")) as
      | AuthResponse<User>
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }
}

// API status checker
// export const checkApiHealth = async (): Promise<boolean> => {
//   try {
//     const response = await authApi('/health');
//     return response.success;
//   } catch {
//     return false;
//   }
// };
